/**
 * Booking Page - Main booking tunnel
 * Updated: Support for 2 service types (Transfer and Hourly)
 * 
 * Flux Transfer (local):
 * 1. Trajet (adresses + véhicule avec prix)
 * 2. Passagers (nombre + options)
 * 3. Tarif (récapitulatif prix)
 * 4. Identité (coordonnées client)
 * 5. Validation (récapitulatif complet + boutons modifier)
 * 6. Paiement
 * 
 * Flux Hourly (mise à disposition):
 * 1. Trajet (adresse + durée + véhicule avec prix)
 * 2. Passagers (nombre + options)
 * 3. Tarif (récapitulatif prix)
 * 4. Identité (coordonnées client)
 * 5. Validation (récapitulatif complet + boutons modifier)
 * 6. Paiement
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BookingProvider, useBooking } from '../../context/BookingContext';
import { useBranding } from '../../context/BrandingContext';
import BookingSteps from '../../components/booking/BookingSteps';
import ServiceSelectionScreen from '../../components/booking/ServiceSelectionScreen';
import Step1Trip from '../../components/booking/Step1Trip';
import Step1Hourly from '../../components/booking/Step1Hourly';
import Step2Details from '../../components/booking/Step2Details';
import Step4Price from '../../components/booking/Step4Price';
import Step5Identity from '../../components/booking/Step5Identity';
import Step6Confirmation from '../../components/booking/Step6Confirmation';
import { SEO } from '../../components/SEO';
import { ArrowLeft, Home } from 'lucide-react';

// Mapping des étapes pour Transfer (local) - 5 étapes seulement
const TRANSFER_STEP_COMPONENTS = {
  1: Step1Trip,        // Trajet + Véhicule
  2: Step2Details,     // Passagers + Options
  3: Step4Price,       // Tarif (ancien Step4)
  4: Step5Identity,    // Identité (ancien Step5)
  5: Step6Confirmation, // Validation + Mode paiement (ancien Step6)
};

// Mapping des étapes pour Hourly (mise à disposition) - 5 étapes seulement
const HOURLY_STEP_COMPONENTS = {
  1: Step1Hourly,       // Adresse + Durée + Véhicule
  2: Step2Details,      // Passagers + Options
  3: Step4Price,        // Tarif
  4: Step5Identity,     // Identité
  5: Step6Confirmation, // Validation + Mode paiement
};

// Noms des étapes
const STEP_NAMES = {
  1: 'Trajet',
  2: 'Passagers',
  3: 'Tarif',
  4: 'Identité',
  5: 'Validation',
};

const BookingContent = () => {
  const [searchParams] = useSearchParams();
  const initialService = searchParams.get('service');
  const [selectedService, setSelectedService] = useState(initialService || null);
  const { currentStep, goToStep, totalSteps, updateBookingData, bookingData } = useBooking();
  const { logoUrl, siteTitle } = useBranding();

  // Mettre à jour le type de service dans le contexte
  useEffect(() => {
    if (selectedService) {
      const serviceType = selectedService === 'hourly' ? 'hourly' : 'transfer';
      updateBookingData({ service_type: serviceType });
    }
  }, [selectedService, updateBookingData]);

  const handleServiceSelect = (serviceId) => {
    setSelectedService(serviceId);
  };

  const handleBackToServices = () => {
    setSelectedService(null);
  };

  const renderStep = () => {
    // Sélectionner le bon mapping selon le service
    const stepComponents = selectedService === 'hourly' 
      ? HOURLY_STEP_COMPONENTS 
      : TRANSFER_STEP_COMPONENTS;
    
    const StepComponent = stepComponents[currentStep];
    if (StepComponent) {
      return <StepComponent />;
    }
    return selectedService === 'hourly' ? <Step1Hourly /> : <Step1Trip />;
  };

  // Show service selection if no service is selected
  const showServiceSelection = !selectedService;

  // Service labels
  const getServiceLabel = () => {
    switch (selectedService) {
      case 'local':
      case 'airport':
        return 'Transfert';
      case 'hourly':
        return 'Mise à disposition';
      case 'excursion':
        return 'Excursion';
      default:
        return 'Réservation';
    }
  };

  return (
    <div className="min-h-screen bg-[#050509]">
      <SEO title="Réserver un VTC en ligne" description="Réservez votre trajet VTC premium Y GO en quelques minutes : transferts, mise à disposition et excursions au Havre et en Normandie. Paiement sécurisé." />
      <h1 className="sr-only" data-testid="booking-page-heading">Réserver votre transfert VTC Y GO</h1>
      {/* Header */}
      <header className="bg-[#0B0B0B] border-b border-[#C9A24D]/10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {showServiceSelection ? (
              <Link 
                to="/" 
                className="flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="text-sm">Accueil</span>
              </Link>
            ) : (
              <button 
                onClick={currentStep === 1 ? handleBackToServices : () => goToStep(currentStep - 1)}
                className="flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">
                  {currentStep === 1 ? 'Choix du service' : 'Retour'}
                </span>
              </button>
            )}
            <Link to="/" className="flex items-center">
              {logoUrl ? (
                <img src={logoUrl} alt={siteTitle || 'Y GO'} className="h-10 w-auto" />
              ) : (
                <span className="font-['Playfair_Display'] text-2xl font-bold text-[#C9A24D]">
                  {siteTitle || 'Y GO'}
                </span>
              )}
            </Link>
            <div className="w-24" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {showServiceSelection ? (
          /* Service Selection Screen */
          <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.12),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm p-6 md:p-8">
            <ServiceSelectionScreen onSelectService={handleServiceSelect} />
          </div>
        ) : (
          <>
            {/* Service Label */}
            <div className="mb-2">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#C9A24D] mb-4">
                {getServiceLabel()}
              </p>
            </div>
            
            {/* Step Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#E5E5E5] text-sm font-medium">
                  {STEP_NAMES[currentStep]}
                </span>
                <span className="text-[#C9A24D]/60 text-xs">
                  Étape {currentStep} sur {totalSteps}
                </span>
              </div>
              <div className="h-1 bg-[#1F1F1F] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#C9A24D] to-[#E6C77A] transition-all duration-500 ease-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Step Content */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.12),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] backdrop-blur-sm p-6 md:p-8">
              {renderStep()}
            </div>
          </>
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-[#E5E5E5]/40 text-xs">
            Réservation sécurisée • Paiement par Stripe
          </p>
          <p className="text-[#E5E5E5]/30 text-xs mt-1">
            © 2026 Y GO Shuttle Services France
          </p>
        </div>
      </main>
    </div>
  );
};

const BookingPage = () => {
  return (
    <BookingProvider>
      <BookingContent />
    </BookingProvider>
  );
};

export default BookingPage;
